import { useMutation } from '@apollo/client/react';
import { PlusIcon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { CREATE_APPENDIX } from './constants';
import ReportTypeSelect from './ReportTypeSelect';
import type { GetLayoutTypes } from './types';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/Dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';

interface NewAppendixDialogProps {
  data: GetLayoutTypes;
  dealOpportunityId: string;
  onSuccess: () => void;
}

const NewAppendixDialog = ({
  data,
  dealOpportunityId,
  onSuccess
}: NewAppendixDialogProps) => {
  const [open, setOpen] = useState(false);
  const [reportTypeCodes, setReportTypeCodes] = useState<string[]>(['icb']);

  const [createAppendix, { loading: isCreatingAppendix }] = useMutation(
    CREATE_APPENDIX,
    {
      onCompleted: () => {
        onSuccess();
      }
    }
  );

  const handleNewAppendixFormSubmit = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);

    try {
      const titleValue = formData.get('title');
      const title = typeof titleValue === 'string' ? titleValue.trim() : '';

      if (!title) {
        throw new Error('Title is required');
      }

      if (!reportTypeCodes.length) {
        throw new Error('At least one Report Type is required');
      }

      const defaultLayoutType = data.layoutTypes[0]?.value;

      if (!defaultLayoutType) {
        throw new Error('Layout type is unavailable');
      }

      const { error: error1 } = await createAppendix({
        variables: {
          dealOpportunityId: dealOpportunityId,
          values: {
            caption: '',
            dealOpportunityId: dealOpportunityId,
            image: '',
            image2: '',
            layoutType: defaultLayoutType,
            reportTypeCodes,
            sequence: 0,
            text: '',
            text2: '',
            text3: '',
            text4: '',
            textHeader1: '',
            textHeader2: '',
            textHeader3: '',
            title: title
          }
        }
      });

      if (error1) {
        throw new Error('An error has occurred');
      }

      setOpen(false);
      setReportTypeCodes(['icb']);
      toast.success('Appendix created successfully');
    } catch (error_) {
      const error = error_ as Error;
      console.error(error);
      toast.error(error.message);
    }
  };

  return (
    <Dialog
      onOpenChange={isOpen => {
        setOpen(isOpen);

        if (!isOpen) {
          setReportTypeCodes(['icb']);
        }
      }}
      open={open}
    >
      <DialogTrigger asChild>
        <Button>
          <PlusIcon />
          New Appendix
        </Button>
      </DialogTrigger>
      <DialogContent
        aria-describedby={undefined}
        className="sm:min-w-[700px]"
      >
        <DialogHeader>
          <DialogTitle>
            Create Appendix
            <Badge variant="yellow">Unsaved</Badge>
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <form
            id="new-appendix-form"
            noValidate
            onSubmit={event => void handleNewAppendixFormSubmit(event)}
          >
            <FieldGroup>
              <div className="flex gap-4">
                <Field className="flex-1">
                  <FieldLabel htmlFor="title-field">Title</FieldLabel>
                  <Input
                    id="title-field"
                    name="title"
                    placeholder="Enter Value"
                  />
                </Field>
                <Field className="flex-1">
                  <ReportTypeSelect
                    onValueChange={setReportTypeCodes}
                    value={reportTypeCodes}
                  />
                </Field>
              </div>
            </FieldGroup>
          </form>
        </DialogBody>
        <DialogFooter className="mt-4 flex justify-end">
          <Button
            disabled={isCreatingAppendix}
            form="new-appendix-form"
            type="submit"
          >
            {isCreatingAppendix && <Spinner />}
            Create Appendix
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NewAppendixDialog;
